import React from "react";
import { Box, Card, Chip, CircularProgress, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import { projectKpis } from "./mapper";
import { enterpriseTokens } from "styles/theme";

const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const formatCrore = (value) => {
    const number = Number(value);
    return `INR ${Number.isFinite(number) ? number.toFixed(2) : "0.00"} Cr`;
};

const getStatus = (item) => {
    const color = String(item.iconBg || "").toUpperCase();

    if (color === "#3CD856") {
        return { label: "On track", color: "#16A36A", soft: "#E9FBF3" };
    }
    if (color === "#FA5A7D") {
        return { label: "Attention", color: "#E5484D", soft: "#FFF0F1" };
    }
    return { label: "Baseline", color: "#E59A16", soft: "#FFF8E8" };
};

const getIcon = (type) => {
    if (type === "progress") return QueryStatsOutlinedIcon;
    if (type === "cost" || type === "percentage") return PaymentsOutlinedIcon;
    return CalendarMonthOutlinedIcon;
};

const getPrimaryValue = (item) => {
    if (item.type === "progress") return `${Number(item.actual || 0).toFixed(2)}${item.unit || ""}`;
    if (item.type === "cost" || item.type === "percentage") return formatCrore(item.value);
    if (item.type === "date") return formatDate(item.value);
    return `${item.value ?? "-"}${item.unit ? ` ${item.unit}` : ""}`;
};

const getSupportingText = (item) => {
    if (item.type === "progress") {
        return `${Number(item.plan || 0).toFixed(2)}${item.unit || ""} ${item.planDisplayText || "planned"}`;
    }
    if (item.type === "cost" && item.subTexts?.length) {
        return item.subTexts.map((value) => `${value.label} ${formatCrore(value.value)}`).join(" | ");
    }
    return item.subText || "Current project baseline";
};

const ProjectLandingKpi = ({ scheduelKpi, costKpi, isLoading, scheduleKpiError, costKpiError }) => {
    const kpiData = projectKpis(scheduelKpi?.data?.[0], costKpi?.data?.[0]);
    const hasError = !isLoading && !kpiData.length && (scheduleKpiError || costKpiError);

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: "6px !important",
                border: `1px solid ${enterpriseTokens.lineSoft} !important`,
                boxShadow: "0 2px 8px rgba(15, 23, 42, 0.05) !important",
                p: 1.5,
                bgcolor: "#FFFFFF",
            }}
        >
            <Box sx={{ mb: 1.25 }}>
                <Typography sx={{ fontSize: 14, lineHeight: "19px", fontWeight: 650, color: enterpriseTokens.navy }}>
                    Delivery Intelligence
                </Typography>
                <Typography sx={{ mt: 0.2, fontSize: 10.5, lineHeight: "15px", color: enterpriseTokens.muted }}>
                    Live schedule, cost, and delivery signals. Icon colors indicate current status.
                </Typography>
            </Box>

            {isLoading && (
                <Box sx={{ minHeight: 96, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress size={22} />
                </Box>
            )}

            {!isLoading && kpiData.length > 0 && (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                            xl: "repeat(7, minmax(0, 1fr))",
                        },
                        gap: 1,
                    }}
                >
                    {kpiData.map((item, index) => {
                        const status = getStatus(item);
                        const Icon = getIcon(item.type);

                        return (
                            <Box
                                key={`${item.key || item.label}-${index}`}
                                sx={{
                                    position: "relative",
                                    minWidth: 0,
                                    minHeight: 112,
                                    p: 1.25,
                                    pb: 1.5,
                                    border: `1px solid ${enterpriseTokens.lineSoft}`,
                                    borderRadius: "3px",
                                    bgcolor: "#FFFFFF",
                                    overflow: "hidden",
                                }}
                            >
                                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                                    <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: status.color, color: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                        <Icon sx={{ fontSize: 16 }} />
                                    </Box>
                                    <Chip
                                        label={status.label}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            borderRadius: "999px",
                                            bgcolor: status.soft,
                                            color: status.color,
                                            border: `1px solid ${status.color}33`,
                                            fontSize: 8.5,
                                            fontWeight: 650,
                                            textTransform: "uppercase",
                                            "& .MuiChip-label": { px: 0.75 },
                                        }}
                                    />
                                </Box>

                                <Typography sx={{ mt: 1, fontSize: 10.5, lineHeight: "14px", fontWeight: 550, color: enterpriseTokens.muted }}>
                                    {item.label}
                                </Typography>
                                <Typography sx={{ mt: 0.25, fontSize: item.type === "date" ? 14 : 16, lineHeight: "21px", fontWeight: 650, color: enterpriseTokens.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {getPrimaryValue(item)}
                                </Typography>
                                <Typography title={getSupportingText(item)} sx={{ mt: 0.3, fontSize: 9.5, lineHeight: "13px", color: enterpriseTokens.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {getSupportingText(item)}
                                </Typography>

                                <Box sx={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 3, bgcolor: status.color }} />
                            </Box>
                        );
                    })}
                </Box>
            )}

            {!isLoading && !kpiData.length && (
                <Box sx={{ minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: hasError ? enterpriseTokens.danger : enterpriseTokens.muted }}>
                    {hasError ? "Unable to load delivery metrics." : "No delivery metrics available."}
                </Box>
            )}
        </Card>
    );
};

export default ProjectLandingKpi;
